import { Component, Inject, OnDestroy } from '@angular/core';
import 'src/app/ext/observable-result';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { ReportFileType } from 'src/app/enum';
import { toReportDay, toReportDayIso } from 'src/app/cards/reports/reports-date.utils';
import { HttpBadRequestResponse, IErrorValue } from 'src/app/_helpers';
import {
  ILocationSimple,
  ISalesReportDto,
  ISalesReportFormData,
  ReportsService
} from 'src/app/_services/reports.service';

export interface ISalesReportExpandDialogData {
  report: ISalesReportDto;
  formData?: ISalesReportFormData;
}

@Component({
  selector: 'sales-report-expand-dialog',
  host: { class: 'dialog' },
  templateUrl: './sales-report-expand.dialog.html',
  styleUrls: ['./sales-report-expand.dialog.scss']
})
export class SalesReportExpandDialog implements OnDestroy {
  public form = new FormGroup({
    from: new FormControl(null),
    to: new FormControl(null),
    fileType: new FormControl([]),
    locations: new FormControl([])
  });

  public fromMin: moment.Moment;
  public fromMax: moment.Moment;
  public toMin: moment.Moment;
  public toMax: moment.Moment;
  public locationOptions: ILocationSimple[] = [];
  public fileTypeOptions: {
    id: ReportFileType;
    name: string;
    icon: string;
  }[] = [];
  public isSubmitting = false;

  public lockedFileTypes: ReportFileType[] = [];
  public lockedLocationIds: string[] = [];
  public allLocationsSelected = false;
  public fromLocked = false;
  public toLocked = false;
  public validationErrors: IErrorValue[] = [];

  private _locationsSub: Subscription;
  private _updateSub: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ISalesReportExpandDialogData,
    public dialogRef: MatDialogRef<SalesReportExpandDialog>,
    private _reportsService: ReportsService
  ) {
    const report = data.report;
    const selected = report?.selectedDataFilters;
    const formData = report?.formData || data.formData;
    this.locationOptions = formData?.locations || [];

    const currentFrom = toReportDay(selected?.from);
    const currentTo = toReportDay(selected?.to);
    const extremeMin = toReportDay(formData?.minReportDate);
    const extremeMax = toReportDay(formData?.maxReportDate);

    this.fromLocked = !currentFrom;
    this.toLocked = !currentTo;

    this.fromMin = extremeMin;
    this.fromMax = currentFrom;
    this.toMin = currentTo;
    this.toMax = extremeMax;

    if (this.fromMin && this.fromMax && this.fromMin.isAfter(this.fromMax)) {
      this.fromMin = this.fromMax.clone();
    }
    if (this.toMin && this.toMax && this.toMin.isAfter(this.toMax)) {
      this.toMax = this.toMin.clone();
    }

    this.lockedFileTypes = [...(selected?.fileTypes || [])];

    const selectedLocations = selected?.locations;
    this.allLocationsSelected = this.hasAllLocations(selectedLocations);
    this.lockedLocationIds = this.allLocationsSelected
      ? []
      : this.toLocationIds(selectedLocations);

    const availableFileTypes = [
      {
        id: ReportFileType.Selver_SalesReport_SalesFile,
        name: 'cards.reports.sales_report_search.sales_data',
        icon: 'report-sales'
      },
      {
        id: ReportFileType.Selver_SalesReport_InventoryFile,
        name: 'cards.reports.sales_report_search.inventory',
        icon: 'report-inventory'
      }
    ];
    this.fileTypeOptions = formData?.fileTypes?.length
      ? availableFileTypes.filter((x) => formData.fileTypes.includes(x.id))
      : availableFileTypes;
    if (!this.fileTypeOptions.length) {
      this.fileTypeOptions = availableFileTypes;
    }

    const lockedKeys = new Set(this.lockedLocationIds.map((id) => id.toLowerCase()));
    const locations = this.allLocationsSelected
      ? []
      : this.locationOptions
          .map((x) => x.id)
          .filter((id) => lockedKeys.has(id?.toLowerCase()));

    this.form.patchValue({
      from: currentFrom,
      to: currentTo,
      fileType: [...this.lockedFileTypes],
      locations
    });

    if (this.fromLocked) {
      this.form.get('from').disable({ emitEvent: false });
    }
    if (this.toLocked) {
      this.form.get('to').disable({ emitEvent: false });
    }

    this._locationsSub = this.form.get('locations').valueChanges.subscribe((value) => {
      this.ensureLockedLocations(value);
    });
  }

  public ngOnDestroy() {
    this._locationsSub?.unsubscribe();
    this._updateSub?.unsubscribe();
  }

  public isFileTypeLocked(fileType: ReportFileType) {
    return this.lockedFileTypes.includes(fileType);
  }

  public hasFileType(fileType: ReportFileType) {
    return (this.form.get('fileType').value || []).includes(fileType);
  }

  public toggleFileType(fileType: ReportFileType) {
    if (this.isSubmitting || this.isFileTypeLocked(fileType)) {
      return;
    }

    const current: ReportFileType[] = [...(this.form.get('fileType').value || [])];
    const index = current.indexOf(fileType);

    if (index >= 0) {
      current.splice(index, 1);
    } else {
      current.push(fileType);
    }

    this.form.patchValue({ fileType: current });
  }

  public close() {
    this.dialogRef.close(false);
  }

  public submit() {
    if (this.isSubmitting || !this.data.report?.id) {
      return;
    }

    this.validationErrors = [];
    const value = this.form.getRawValue();
    this.isSubmitting = true;
    this._updateSub?.unsubscribe();
    this._updateSub = this._reportsService
      .updateSalesReport(this.data.report.id, {
        id: this.data.report.id,
        from: toReportDayIso(value.from) || undefined,
        to: toReportDayIso(value.to) || undefined,
        fileTypes: value.fileType || [],
        locationIds: this.toLocationIds(value.locations).length
          ? this.toLocationIds(value.locations)
          : null
      })
      .result(
        this.form,
        () => {
          this.dialogRef.close({
            from: value.from,
            to: value.to,
            fileType: value.fileType,
            locations: value.locations
          });
        },
        (error) => {
          this.isSubmitting = false;
          if (!(error instanceof HttpBadRequestResponse)) {
            return;
          }

          this.applyValidationAliases(error.validationResults);
          this.validationErrors = this.uniqueValidationErrors(error.validationResults);
        }
      );
  }

  private applyValidationAliases(results?: { [id: string]: IErrorValue[] }) {
    if (!results) {
      return;
    }

    if (results.fileTypes) {
      this.form.get('fileType')?.setErrors(results.fileTypes);
    }
    if (results.locationIds) {
      this.form.get('locations')?.setErrors(results.locationIds);
    }
  }

  private uniqueValidationErrors(results?: { [id: string]: IErrorValue[] }): IErrorValue[] {
    const seen = new Set<string>();
    const errors: IErrorValue[] = [];

    Object.keys(results || {}).forEach((key) => {
      (results[key] || []).forEach((error) => {
        if (!error?.value || seen.has(error.value)) {
          return;
        }
        seen.add(error.value);
        errors.push(error);
      });
    });

    return errors;
  }

  private hasAllLocations(selected: Array<ILocationSimple | string> | null | undefined) {
    if (selected == null) {
      return true;
    }

    const availableIds = this.toLocationIds(this.locationOptions);
    if (!availableIds.length) {
      return false;
    }

    const selectedIds = new Set(this.toLocationIds(selected).map((id) => id.toLowerCase()));
    return availableIds.every((id) => selectedIds.has(id.toLowerCase()));
  }

  private ensureLockedLocations(value: Array<ILocationSimple | string>) {
    if (!this.lockedLocationIds.length) {
      return;
    }

    const current = value || [];
    if (!current.length) {
      return;
    }

    const currentIds = new Set(this.toLocationIds(current).map((id) => id.toLowerCase()));
    const missing = this.lockedLocationIds.filter(
      (id) => !currentIds.has(id.toLowerCase())
    );

    if (!missing.length) {
      return;
    }

    const missingKeys = new Set(missing.map((id) => id.toLowerCase()));
    const restored = this.locationOptions
      .map((x) => x.id)
      .filter((id) => missingKeys.has(id?.toLowerCase()));
    this.form.patchValue(
      { locations: [...restored, ...current] },
      { emitEvent: false }
    );
  }

  private toLocationIds(value?: Array<ILocationSimple | string> | null): string[] {
    return (value || [])
      .map((x) => (typeof x === 'string' ? x : x?.id))
      .filter((id): id is string => !!id);
  }
}
